import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Send, Users, MessageSquare, Image, CheckCircle, XCircle, 
  Loader2, RefreshCw, Zap, Clock, AlertTriangle, ChevronRight,
  UsersRound, Radio, Filter, Search, Sparkles, FileText,
  Pause, Play, Square, Trash2, ArrowLeft, ArrowRight, History
} from 'lucide-react';

export default function BulkSendPage() {
  // Estados principais
  const [connections, setConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState('');
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  
  // Templates
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  
  // IA
  const [enhancingText, setEnhancingText] = useState(false);
  
  // Estados de extração
  const [extractedNumbers, setExtractedNumbers] = useState([]);
  const [selectedNumbers, setSelectedNumbers] = useState(new Set()); // Para seleção múltipla
  const [extracting, setExtracting] = useState(false);
  const [extractProgress, setExtractProgress] = useState(0);
  const [numberSearchTerm, setNumberSearchTerm] = useState(''); // Filtro de busca
  
  // Estados de mensagem
  const [messageType, setMessageType] = useState('text');
  const [messageText, setMessageText] = useState('');
  const [imageBase64, setImageBase64] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [caption, setCaption] = useState('');
  const [buttonChoices, setButtonChoices] = useState(['']);
  const [listButtonText, setListButtonText] = useState('Ver Opções');
  const [footerText, setFooterText] = useState('');
  const [delayMin, setDelayMin] = useState(5);
  const [delayMax, setDelayMax] = useState(15);
  
  // Estados de agendamento em lotes
  const [useBatchSchedule, setUseBatchSchedule] = useState(false);
  const [batchSize, setBatchSize] = useState(50);
  
  // Estados de variação por IA
  const [useAiVariation, setUseAiVariation] = useState(false);
  
  // Estados de envio e progresso em tempo real
  const [activeBulkSend, setActiveBulkSend] = useState(null);
  const [bulkProgress, setBulkProgress] = useState(null);
  const [sendHistory, setSendHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  
  // Polling interval
  const pollIntervalRef = useRef(null);
  
  // Filtro de grupos
  const [groupSearch, setGroupSearch] = useState('');
  
  // Step atual (1-4 = criação, 5 = progresso)
  const [currentStep, setCurrentStep] = useState(1);

  useEffect(() => {
    fetchConnections();
    fetchTemplates();
    checkActiveBulkSend();
    
    // Cleanup polling on unmount
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Carregar grupos automaticamente ao selecionar conexão
  useEffect(() => {
    if (selectedConnection) {
      loadGroupsFromDB(selectedConnection);
    } else {
      setGroups([]);
      setSelectedGroups([]);
    }
  }, [selectedConnection]);

  const fetchConnections = async () => {
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/connections?owner_filter=mine`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Filtrar apenas conexões conectadas
        const connected = data.filter(c => 
          c.status === 'connected' || 
          (typeof c.status === 'object' && c.status?.connected)
        );
        setConnections(connected);
        
        // Auto-selecionar se tiver apenas 1 conexão
        if (connected.length === 1) {
          setSelectedConnection(connected[0].id);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar conexões:', error);
      toast.error('Erro ao carregar conexões');
    }
  };

  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/templates?owner_filter=mine`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Carregar grupos do banco de dados (já sincronizados)
  const loadGroupsFromDB = async (connectionId) => {
    setLoadingGroups(true);
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/connections/${connectionId}/groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        // Transformar para o formato esperado
        const formattedGroups = data.map(g => ({
          jid: g.group_id,
          name: g.name,
          participants_count: g.participants_count || 0,
          is_community: false
        }));
        setGroups(formattedGroups);
        if (formattedGroups.length === 0) {
          toast.info('Nenhum grupo sincronizado. Clique em "Atualizar Grupos" para buscar da API.');
        }
      }
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
    } finally {
      setLoadingGroups(false);
    }
  };

  // Atualizar grupos da UAZAPI (força refresh)
  const refreshGroupsFromAPI = async () => {
    if (!selectedConnection) return;
    
    setLoadingGroups(true);
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/connections/${selectedConnection}/refresh-groups`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.synced_count || 0} grupos sincronizados!`);
        // Recarregar do banco
        await loadGroupsFromDB(selectedConnection);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao atualizar grupos');
      }
    } catch (error) {
      console.error('Erro ao atualizar grupos:', error);
      toast.error('Erro ao atualizar grupos');
    } finally {
      setLoadingGroups(false);
    }
  };

  const checkActiveBulkSend = async () => {
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bulk-send/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.active) {
          setActiveBulkSend(data.active);
          setCurrentStep(5); // Redirecionar para tela de progresso
          startProgressPolling(data.active.id);
        }
      }
    } catch (error) {
      console.error('Erro ao verificar envio ativo:', error);
    }
  };

  const startProgressPolling = useCallback((bulkId) => {
    // Limpar polling anterior
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    
    const fetchProgress = async () => {
      try {
        const token = localStorage.getItem('nexus-token');
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bulk-send/${bulkId}/progress`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setBulkProgress(data);
          
          // Se completou ou foi cancelado, parar polling
          if (data.status === 'completed' || data.status === 'cancelled') {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        }
      } catch (error) {
        console.error('Erro ao buscar progresso:', error);
      }
    };
    
    // Buscar imediatamente
    fetchProgress();
    
    // Polling a cada 2 segundos
    pollIntervalRef.current = setInterval(fetchProgress, 2000);
  }, []);

  const extractParticipants = async () => {
    if (selectedGroups.length === 0) {
      toast.error('Selecione pelo menos um grupo');
      return;
    }
    
    setExtracting(true);
    setExtractProgress(0);
    
    try {
      const token = localStorage.getItem('nexus-token');
      
      // Simular progresso
      const progressInterval = setInterval(() => {
        setExtractProgress(prev => Math.min(prev + 10, 90));
      }, 500);
      
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bulk-send/extract-participants`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          connection_id: selectedConnection,
          group_jids: selectedGroups
        })
      });
      
      clearInterval(progressInterval);
      setExtractProgress(100);
      
      if (res.ok) {
        const data = await res.json();
        setExtractedNumbers(data.participants || []);
        toast.success(`${data.total_participants} participantes extraídos de ${data.successful_groups} grupos!`);
        setCurrentStep(2);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao extrair participantes');
      }
    } catch (error) {
      console.error('Erro ao extrair:', error);
      toast.error('Erro ao extrair participantes');
    } finally {
      setExtracting(false);
    }
  };

  // Funções de gerenciamento de números
  const removeNumber = (numberToRemove) => {
    setExtractedNumbers(prev => prev.filter(num => num !== numberToRemove));
    setSelectedNumbers(prev => {
      const newSet = new Set(prev);
      newSet.delete(numberToRemove);
      return newSet;
    });
  };

  const removeSelectedNumbers = () => {
    if (selectedNumbers.size === 0) {
      toast.error('Selecione números para remover');
      return;
    }
    setExtractedNumbers(prev => prev.filter(num => !selectedNumbers.has(num)));
    setSelectedNumbers(new Set());
    toast.success(`${selectedNumbers.size} números removidos`);
  };

  const toggleNumberSelection = (number) => {
    setSelectedNumbers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(number)) {
        newSet.delete(number);
      } else {
        newSet.add(number);
      }
      return newSet;
    });
  };

  const selectAllNumbers = () => {
    const filteredNumbers = extractedNumbers.filter(num => 
      num.includes(numberSearchTerm)
    );
    if (selectedNumbers.size === filteredNumbers.length) {
      setSelectedNumbers(new Set()); // Desmarcar todos
    } else {
      setSelectedNumbers(new Set(filteredNumbers)); // Marcar todos filtrados
    }
  };

  const filteredExtractedNumbers = extractedNumbers.filter(num => 
    num.includes(numberSearchTerm)
  );

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageBase64(reader.result);
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const enhanceTextWithAI = async () => {
    const text = messageType === 'image' ? caption : messageText;
    if (!text?.trim()) {
      toast.error('Digite uma mensagem primeiro');
      return;
    }
    
    setEnhancingText(true);
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/enhance-text`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text })
      });
      
      if (res.ok) {
        const data = await res.json();
        if (messageType === 'image') {
          setCaption(data.enhanced);
        } else {
          setMessageText(data.enhanced);
        }
        toast.success('Texto melhorado com IA!');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao melhorar texto');
      }
    } catch (error) {
      toast.error('Erro ao conectar com IA');
    } finally {
      setEnhancingText(false);
    }
  };

  const applyTemplate = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      if (messageType === 'image') {
        setCaption(template.message);
      } else {
        setMessageText(template.message);
      }
      toast.success(`Template "${template.name}" aplicado!`);
    }
  };

  const sendBulkMessages = async () => {
    if (extractedNumbers.length === 0) {
      toast.error('Nenhum número para enviar');
      return;
    }
    
    if (messageType === 'text' && !messageText) {
      toast.error('Digite uma mensagem');
      return;
    }
    
    if (messageType === 'image' && !imageBase64) {
      toast.error('Selecione uma imagem');
      return;
    }
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem('nexus-token');
      
      const payload = {
        connection_id: selectedConnection,
        numbers: extractedNumbers,  // Usar direto sem validação
        message_type: messageType,
        text: messageText,
        image_base64: messageType === 'image' ? imageBase64 : null,
        caption: caption,
        button_choices: buttonChoices.filter(b => b.trim()),
        list_button_text: listButtonText,
        footer_text: footerText,
        delay_min: delayMin,
        delay_max: delayMax,
        // Agendamento em lotes
        use_batch_schedule: useBatchSchedule,
        batch_size: useBatchSchedule ? batchSize : null,
        // Variação por IA
        use_ai_variation: useAiVariation
      };
      
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bulk-send/send`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Envio em massa iniciado!');
        
        // Buscar dados completos do bulk send criado
        const bulkRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bulk-send/${data.bulk_send_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (bulkRes.ok) {
          const bulkData = await bulkRes.json();
          setActiveBulkSend(bulkData);
        }
        
        // Ir para tela de progresso
        setCurrentStep(5);
        startProgressPolling(data.bulk_send_id);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao enviar mensagens');
      }
    } catch (error) {
      console.error('Erro ao enviar:', error);
      toast.error('Erro ao enviar mensagens');
    } finally {
      setLoading(false);
    }
  };

  // Controles de envio ativo
  const pauseBulkSend = async () => {
    if (!activeBulkSend) return;
    
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bulk-send/${activeBulkSend.id}/pause`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Envio pausado!');
        setActiveBulkSend(prev => ({ ...prev, status: 'paused' }));
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao pausar');
      }
    } catch (error) {
      toast.error('Erro ao pausar envio');
    }
  };

  const resumeBulkSend = async () => {
    if (!activeBulkSend) return;
    
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bulk-send/${activeBulkSend.id}/resume`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Envio retomado!');
        setActiveBulkSend(prev => ({ ...prev, status: 'sending' }));
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao retomar');
      }
    } catch (error) {
      toast.error('Erro ao retomar envio');
    }
  };

  const cancelBulkSend = async () => {
    if (!activeBulkSend) return;
    
    if (!window.confirm('Tem certeza que deseja cancelar o envio? Esta ação não pode ser desfeita.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bulk-send/${activeBulkSend.id}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Envio cancelado!');
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
        setActiveBulkSend(prev => ({ ...prev, status: 'cancelled' }));
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao cancelar');
      }
    } catch (error) {
      toast.error('Erro ao cancelar envio');
    }
  };

  const deleteBulkSend = async (bulkId) => {
    if (!window.confirm('Tem certeza que deseja excluir este registro?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bulk-send/${bulkId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Registro excluído!');
        if (activeBulkSend?.id === bulkId) {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
          }
          setActiveBulkSend(null);
          setBulkProgress(null);
          setCurrentStep(1);
        }
        fetchHistory();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao excluir');
      }
    } catch (error) {
      toast.error('Erro ao excluir registro');
    }
  };

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bulk-send/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSendHistory(data);
      }
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    }
  };

  const filteredGroups = groups.filter(g => 
    g.name?.toLowerCase().includes(groupSearch.toLowerCase())
  );

  const toggleSelectAll = () => {
    if (selectedGroups.length === filteredGroups.length) {
      setSelectedGroups([]);
    } else {
      setSelectedGroups(filteredGroups.map(g => g.jid));
    }
  };

  const resetAll = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    setSelectedGroups([]);
    setExtractedNumbers([]);
    setSelectedNumbers(new Set());
    setNumberSearchTerm('');
    setMessageText('');
    setImageBase64('');
    setImagePreview('');
    setCaption('');
    setUseBatchSchedule(false);
    setBatchSize(50);
    setUseAiVariation(false);
    setActiveBulkSend(null);
    setBulkProgress(null);
    setCurrentStep(1);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sending':
      case 'scheduled':
        return 'bg-blue-500';
      case 'paused':
        return 'bg-yellow-500';
      case 'completed':
        return 'bg-green-500';
      case 'cancelled':
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'sending':
        return 'Enviando';
      case 'scheduled':
        return 'Agendado';
      case 'paused':
        return 'Pausado';
      case 'completed':
        return 'Concluído';
      case 'cancelled':
        return 'Cancelado';
      case 'error':
        return 'Erro';
      default:
        return status;
    }
  };

  return (
    <div className="space-y-6 p-6" data-testid="bulk-send-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Envio em Massa
          </h1>
          <p className="text-xs sm:text-base text-muted-foreground mt-0.5 sm:mt-1">
            Extraia participantes de grupos e envie mensagens
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm"
            className="text-xs sm:text-sm h-8 sm:h-10"
            onClick={() => {
              setShowHistory(true);
              fetchHistory();
            }}
          >
            <History className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Histórico</span>
            <span className="sm:hidden">Hist.</span>
          </Button>
          {currentStep !== 5 && (
            <Button variant="outline" size="sm" onClick={resetAll} className="text-xs sm:text-sm h-8 sm:h-10">
              <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Recomeçar</span>
              <span className="sm:hidden">Reset</span>
            </Button>
          )}
        </div>
      </div>

      {/* Progress Steps - só mostra se não está na tela de progresso */}
      {currentStep < 5 && (
        <div className="flex items-center justify-between sm:justify-center gap-1 sm:gap-2 p-3 sm:p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl border border-purple-500/20 overflow-x-auto">
          {[
            { step: 1, label: 'Grupos', icon: UsersRound },
            { step: 2, label: 'Extrair', icon: Users },
            { step: 3, label: 'Números', icon: CheckCircle },
            { step: 4, label: 'Enviar', icon: Send }
          ].map((item, index) => (
            <div key={item.step} className="flex items-center shrink-0">
              <div className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all ${
                currentStep >= item.step 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-muted text-muted-foreground'
              }`}>
                <item.icon className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="text-xs sm:text-sm font-medium">{item.label}</span>
              </div>
              {index < 3 && <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 mx-0.5 sm:mx-2 text-muted-foreground" />}
            </div>
          ))}
        </div>
      )}

      {/* Step 1: Selecionar Conexão e Grupos */}
      {currentStep === 1 && (
        <div className="space-y-6">
          {/* Seleção de Conexão */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Radio className="w-5 h-5 text-purple-500" />
                Selecione a Conexão
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Select value={selectedConnection} onValueChange={setSelectedConnection}>
                  <SelectTrigger className="w-full sm:flex-1" data-testid="connection-select">
                    <SelectValue placeholder="Selecione uma conexão WhatsApp" />
                  </SelectTrigger>
                  <SelectContent>
                    {connections.map(conn => (
                      <SelectItem key={conn.id} value={conn.id}>
                        {conn.name || conn.instance_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Button 
                  onClick={refreshGroupsFromAPI} 
                  disabled={!selectedConnection || loadingGroups}
                  variant="outline"
                  className="w-full sm:w-auto"
                  data-testid="refresh-groups-btn"
                >
                  {loadingGroups ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  <span className="sm:inline">Atualizar Grupos</span>
                </Button>
              </div>
              
              {connections.length === 0 && (
                <p className="text-sm text-amber-600">
                  Nenhuma conexão WhatsApp conectada. Conecte uma instância primeiro.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Lista de Grupos */}
          {(groups.length > 0 || (selectedConnection && loadingGroups)) && (
            <Card>
              <CardHeader className="pb-3 sm:pb-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                    <UsersRound className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" />
                    Grupos ({groups.length})
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-purple-500/20 text-xs">
                      {selectedGroups.length} selecionados
                    </Badge>
                    <Button variant="outline" size="sm" onClick={toggleSelectAll} className="text-xs sm:text-sm">
                      {selectedGroups.length === filteredGroups.length ? 'Desmarcar' : 'Selecionar'} Todos
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Busca */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar grupos..."
                    value={groupSearch}
                    onChange={(e) => setGroupSearch(e.target.value)}
                    className="pl-10"
                    data-testid="group-search"
                  />
                </div>
                
                {/* Lista de Grupos */}
                <div className="max-h-[300px] sm:max-h-[400px] overflow-y-auto space-y-2 pr-1 sm:pr-2">
                  {filteredGroups.map(group => (
                    <div
                      key={group.jid}
                      className={`flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedGroups.includes(group.jid)
                          ? 'border-purple-500 bg-purple-500/10'
                          : 'border-border hover:border-purple-500/50 hover:bg-muted/50'
                      }`}
                      onClick={() => {
                        if (selectedGroups.includes(group.jid)) {
                          setSelectedGroups(selectedGroups.filter(j => j !== group.jid));
                        } else {
                          setSelectedGroups([...selectedGroups, group.jid]);
                        }
                      }}
                      data-testid={`group-item-${group.jid}`}
                    >
                      <Checkbox checked={selectedGroups.includes(group.jid)} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm sm:text-base truncate">{group.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {group.participants_count} participantes
                          {group.is_community && ' • Comunidade'}
                        </p>
                      </div>
                      <Badge variant="outline" className="shrink-0 text-xs">
                        {group.participants_count}
                      </Badge>
                    </div>
                  ))}
                </div>
                
                {/* Botão Extrair */}
                <Button
                  onClick={extractParticipants}
                  disabled={selectedGroups.length === 0 || extracting}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 h-10 sm:h-12 text-sm sm:text-lg"
                  data-testid="extract-btn"
                >
                  {extracting ? (
                    <>
                      <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin mr-2" />
                      Extraindo... {extractProgress.toFixed(0)}%
                    </>
                  ) : (
                    <>
                      <Users className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                      <span className="hidden sm:inline">Extrair Participantes de </span>
                      {selectedGroups.length} Grupos
                    </>
                  )}
                </Button>
                
                {extracting && (
                  <Progress value={extractProgress} className="h-2" />
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Step 2: Extração concluída - Validar */}
      {currentStep === 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
              Participantes Extraídos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 sm:space-y-6">
            <div className="flex items-center justify-center gap-4 sm:gap-8 p-4 sm:p-6 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-xl border border-green-500/20">
              <div className="text-center">
                <p className="text-2xl sm:text-4xl font-bold text-green-600">{extractedNumbers.length}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">Números Extraídos</p>
              </div>
              <div className="text-center">
                <p className="text-2xl sm:text-4xl font-bold text-purple-600">{selectedGroups.length}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">Grupos Processados</p>
              </div>
            </div>
            
            <div className="p-3 sm:p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-xs sm:text-sm text-amber-700 dark:text-amber-300 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>Agora vamos validar quais números estão ativos no WhatsApp</span>
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(1)}
                className="w-full sm:flex-1 order-2 sm:order-1"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
              <Button
                onClick={() => setCurrentStep(3)}
                disabled={extractedNumbers.length === 0}
                className="w-full sm:flex-1 bg-gradient-to-r from-green-600 to-emerald-600 h-10 sm:h-12 text-sm sm:text-lg order-1 sm:order-2"
                data-testid="continue-btn"
              >
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Prosseguir com {extractedNumbers.length} Números
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Números extraídos - Criar mensagem */}
      {currentStep === 3 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Gerenciar Números */}
          <Card>
            <CardHeader className="pb-3 sm:pb-6">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                Números para Envio
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 sm:space-y-4">
              {/* Contador */}
              <div className="p-3 sm:p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg text-center">
                <p className="text-2xl sm:text-4xl font-bold text-blue-600">{extractedNumbers.length}</p>
                <p className="text-xs sm:text-sm text-muted-foreground flex items-center justify-center gap-1">
                  <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" /> Números para Envio
                </p>
              </div>
              
              {/* Barra de busca e ações */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar número..."
                    value={numberSearchTerm}
                    onChange={(e) => setNumberSearchTerm(e.target.value)}
                    className="pl-9 text-sm"
                  />
                </div>
              </div>
              
              {/* Ações em lote */}
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectAllNumbers}
                  className="text-xs"
                >
                  {selectedNumbers.size === filteredExtractedNumbers.length && filteredExtractedNumbers.length > 0
                    ? 'Desmarcar Todos' 
                    : `Selecionar (${filteredExtractedNumbers.length})`}
                </Button>
                {selectedNumbers.size > 0 && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={removeSelectedNumbers}
                    className="text-xs"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Remover {selectedNumbers.size}
                  </Button>
                )}
              </div>
              
              {/* Lista de números */}
              <div className="max-h-[200px] sm:max-h-[280px] overflow-y-auto space-y-1 p-2 bg-muted/50 rounded-lg">
                {filteredExtractedNumbers.map((num, i) => (
                  <div 
                    key={i} 
                    className={`flex items-center justify-between text-xs sm:text-sm py-1.5 sm:py-2 px-2 sm:px-3 rounded cursor-pointer transition-colors ${
                      selectedNumbers.has(num) 
                        ? 'bg-blue-500/20 border border-blue-500/50' 
                        : 'bg-background hover:bg-muted'
                    }`}
                    onClick={() => toggleNumberSelection(num)}
                  >
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={selectedNumbers.has(num)}
                        onCheckedChange={() => toggleNumberSelection(num)}
                        onClick={(e) => e.stopPropagation()}
                        className="h-4 w-4"
                      />
                      <span className="font-mono text-xs sm:text-sm">{num}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeNumber(num);
                      }}
                      className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-100"
                    >
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                {filteredExtractedNumbers.length === 0 && numberSearchTerm && (
                  <p className="text-xs sm:text-sm text-center text-muted-foreground py-4">
                    Nenhum número encontrado com "{numberSearchTerm}"
                  </p>
                )}
                {filteredExtractedNumbers.length === 0 && !numberSearchTerm && (
                  <p className="text-xs sm:text-sm text-center text-muted-foreground py-4">
                    Nenhum número na lista
                  </p>
                )}
              </div>
              
              <Button
                variant="outline"
                onClick={() => setCurrentStep(2)}
                className="w-full"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            </CardContent>
          </Card>

          {/* Editor de Mensagem */}
          <Card>
            <CardHeader className="pb-3 sm:pb-6">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" />
                Criar Mensagem
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 sm:space-y-4">
              {/* Tipo de mensagem */}
              <div className="space-y-2">
                <Label className="text-sm">Tipo de Mensagem</Label>
                <Select value={messageType} onValueChange={setMessageType}>
                  <SelectTrigger data-testid="message-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Texto Simples</SelectItem>
                    <SelectItem value="image">Imagem com Legenda</SelectItem>
                    <SelectItem value="button">Botões Interativos</SelectItem>
                    <SelectItem value="list">Lista/Menu</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Template e IA */}
              <div className="flex flex-col sm:flex-row gap-2">
                {templates.length > 0 && (
                  <Select onValueChange={applyTemplate}>
                    <SelectTrigger className="w-full sm:flex-1" data-testid="template-select">
                      <SelectValue placeholder="Usar Template" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map(t => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={enhanceTextWithAI}
                  disabled={enhancingText || !(messageType === 'image' ? caption : messageText)?.trim()}
                  className="w-full sm:w-auto text-purple-500 hover:text-purple-400 border-purple-500/30 text-sm"
                  data-testid="enhance-ai-btn"
                >
                  {enhancingText ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      <span className="hidden sm:inline">Melhorando...</span>
                      <span className="sm:hidden">IA...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-1" />
                      <span className="hidden sm:inline">Melhorar com IA</span>
                      <span className="sm:hidden">IA</span>
                    </>
                  )}
                </Button>
              </div>
              
              {/* Texto */}
              <div className="space-y-2">
                <Label>{messageType === 'image' ? 'Legenda' : 'Mensagem'}</Label>
                <Textarea
                  placeholder="Digite sua mensagem..."
                  value={messageType === 'image' ? caption : messageText}
                  onChange={(e) => messageType === 'image' ? setCaption(e.target.value) : setMessageText(e.target.value)}
                  rows={4}
                  data-testid="message-textarea"
                />
              </div>
              
              {/* Upload de imagem */}
              {messageType === 'image' && (
                <div className="space-y-2">
                  <Label>Imagem</Label>
                  <Input type="file" accept="image/*" onChange={handleImageUpload} data-testid="image-upload" />
                  {imagePreview && (
                    <img src={imagePreview} alt="Preview" className="max-h-32 rounded-lg" />
                  )}
                </div>
              )}
              
              {/* Botões */}
              {(messageType === 'button' || messageType === 'list') && (
                <div className="space-y-2">
                  <Label>Opções (máx. 3)</Label>
                  {buttonChoices.map((choice, i) => (
                    <div key={i} className="flex gap-2">
                      <Input
                        placeholder={`Opção ${i + 1}`}
                        value={choice}
                        onChange={(e) => {
                          const newChoices = [...buttonChoices];
                          newChoices[i] = e.target.value;
                          setButtonChoices(newChoices);
                        }}
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="text-red-500 hover:text-red-600 hover:bg-red-500/10 shrink-0"
                        onClick={() => {
                          const newChoices = buttonChoices.filter((_, idx) => idx !== i);
                          setButtonChoices(newChoices.length > 0 ? newChoices : ['']);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {buttonChoices.length < 3 && (
                    <Button variant="outline" size="sm" onClick={() => setButtonChoices([...buttonChoices, ''])}>
                      + Adicionar Opção
                    </Button>
                  )}
                </div>
              )}
              
              {messageType === 'list' && (
                <div className="space-y-2">
                  <Label>Texto do Botão</Label>
                  <Input
                    value={listButtonText}
                    onChange={(e) => setListButtonText(e.target.value)}
                    placeholder="Ver Opções"
                  />
                </div>
              )}
              
              {/* Delay */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-1 sm:space-y-2">
                  <Label className="text-xs sm:text-sm">Delay Mín (seg)</Label>
                  <Input
                    type="number"
                    value={delayMin}
                    onChange={(e) => setDelayMin(parseInt(e.target.value) || 5)}
                    min={1}
                    className="h-9 sm:h-10"
                  />
                </div>
                <div className="space-y-1 sm:space-y-2">
                  <Label className="text-xs sm:text-sm">Delay Máx (seg)</Label>
                  <Input
                    type="number"
                    value={delayMax}
                    onChange={(e) => setDelayMax(parseInt(e.target.value) || 15)}
                    min={1}
                    className="h-9 sm:h-10"
                  />
                </div>
              </div>
              
              {/* Variação por IA - Anti-Spam */}
              {(messageType === 'text' || messageType === 'button' || messageType === 'list') && (
                <div className="space-y-2 sm:space-y-3 p-3 sm:p-4 border border-purple-500/30 rounded-lg bg-purple-500/5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                      <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" />
                      <Label className="text-sm sm:text-base font-medium">Variação por IA</Label>
                      <Badge variant="outline" className="text-[10px] sm:text-xs text-purple-600 border-purple-500/50">Anti-Spam</Badge>
                    </div>
                    <Checkbox
                      checked={useAiVariation}
                      onCheckedChange={setUseAiVariation}
                    />
                  </div>
                  
                  {useAiVariation && (
                    <div className="space-y-2 pt-1 sm:pt-2">
                      <div className="p-2 sm:p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          <strong className="text-purple-600">Como funciona:</strong> A IA cria uma versão única 
                          para cada contato, mantendo o mesmo sentido.
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-2 text-[10px] sm:text-xs text-muted-foreground">
                        <CheckCircle className="w-3 h-3 text-green-500 shrink-0" />
                        <span>Mantém variáveis {'{nome}'}, {'{plano}'}, etc.</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Dica de segurança */}
              <div className="p-2 sm:p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500 shrink-0 mt-0.5" />
                  <div className="text-xs sm:text-sm">
                    <p className="font-medium text-amber-600">Dica de Segurança</p>
                    <p className="text-muted-foreground">
                      Para evitar bloqueios do WhatsApp, recomendamos enviar no máximo <strong>50 mensagens por hora</strong>. 
                      Use o agendamento em lotes abaixo para envios maiores.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Agendamento em Lotes */}
              <div className="space-y-2 sm:space-y-3 p-3 sm:p-4 border rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 sm:gap-2">
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                    <Label className="text-sm sm:text-base font-medium">Agendamento em Lotes</Label>
                  </div>
                  <Checkbox
                    checked={useBatchSchedule}
                    onCheckedChange={setUseBatchSchedule}
                  />
                </div>
                
                {useBatchSchedule && (
                  <div className="space-y-2 sm:space-y-3 pt-1 sm:pt-2">
                    <div className="space-y-1 sm:space-y-2">
                      <Label className="text-xs sm:text-sm">Números por Lote (1-50)</Label>
                      <Input
                        type="number"
                        value={batchSize}
                        onChange={(e) => setBatchSize(Math.min(50, Math.max(1, parseInt(e.target.value) || 50)))}
                        min={1}
                        max={50}
                        className="h-9 sm:h-10"
                      />
                    </div>
                    
                    {/* Preview dos lotes */}
                    <div className="p-2 sm:p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Radio className="w-3 h-3 sm:w-4 sm:h-4 text-blue-500" />
                        <span className="text-xs sm:text-sm font-medium">Preview</span>
                      </div>
                      <div className="text-xs sm:text-sm text-muted-foreground grid grid-cols-2 gap-1">
                        <p>Números: <strong>{extractedNumbers.length}</strong></p>
                        <p>Por lote: <strong>{batchSize}</strong></p>
                        <p>Lotes: <strong>{Math.ceil(extractedNumbers.length / batchSize)}</strong></p>
                        <p>Intervalo: <strong>1h</strong></p>
                      </div>
                      <p className="text-xs text-blue-600 mt-1">
                        Tempo: ~{Math.ceil(extractedNumbers.length / batchSize) - 1}h
                      </p>
                    </div>
                    
                    <p className="text-[10px] sm:text-xs text-muted-foreground">
                      Primeiro lote imediato. Próximos a cada 1 hora.
                    </p>
                  </div>
                )}
              </div>
              
              {/* Botão Enviar */}
              <Button
                onClick={sendBulkMessages}
                disabled={loading || extractedNumbers.length === 0}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 h-10 sm:h-12 text-sm sm:text-lg"
                data-testid="send-bulk-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin mr-2" />
                    Enviando...
                  </>
                ) : useBatchSchedule ? (
                  <>
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    Agendar {Math.ceil(extractedNumbers.length / batchSize)} Lotes
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    Enviar para {extractedNumbers.length}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 5: Tela de Progresso em Tempo Real */}
      {currentStep === 5 && activeBulkSend && (
        <Card>
          <CardHeader className="pb-3 sm:pb-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-purple-500" />
                <span className="hidden sm:inline">Envio em Massa em Progresso</span>
                <span className="sm:hidden">Envio em Progresso</span>
              </CardTitle>
              <Badge className={`${getStatusColor(bulkProgress?.status || activeBulkSend.status)} text-white text-xs`}>
                {getStatusText(bulkProgress?.status || activeBulkSend.status)}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 sm:space-y-6">
            {/* Barra de progresso */}
            <div className="space-y-1 sm:space-y-2">
              <div className="flex justify-between text-xs sm:text-sm">
                <span>Progresso</span>
                <span className="font-bold">{bulkProgress?.progress_percent || 0}%</span>
              </div>
              <Progress value={bulkProgress?.progress_percent || 0} className="h-3 sm:h-4" />
            </div>
            
            {/* Estatísticas em tempo real */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
              <div className="p-2 sm:p-4 bg-muted/50 rounded-lg text-center">
                <p className="text-lg sm:text-2xl font-bold">{bulkProgress?.total || activeBulkSend.total_numbers}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">Total</p>
              </div>
              <div className="p-2 sm:p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-center">
                <p className="text-lg sm:text-2xl font-bold text-green-600">{bulkProgress?.sent || 0}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">Enviados</p>
              </div>
              <div className="p-2 sm:p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-center">
                <p className="text-lg sm:text-2xl font-bold text-red-600">{bulkProgress?.failed || 0}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">Falhas</p>
              </div>
              <div className="p-2 sm:p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg text-center">
                <p className="text-lg sm:text-2xl font-bold text-blue-600">{bulkProgress?.pending || 0}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">Pendentes</p>
              </div>
            </div>
            
            {/* Info do delay */}
            <div className="p-2 sm:p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-xs sm:text-sm text-amber-700 dark:text-amber-300 flex items-center gap-2">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" />
                Delay: {activeBulkSend.delay_min}s - {activeBulkSend.delay_max}s
              </p>
            </div>
            
            {/* Info de lotes se aplicável */}
            {activeBulkSend.use_batch_schedule && activeBulkSend.batches && (
              <div className="p-3 sm:p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg space-y-2 sm:space-y-3">
                <div className="flex items-center gap-2">
                  <Radio className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                  <span className="text-sm sm:text-base font-medium">Lotes</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 text-xs sm:text-sm">
                  <div>
                    <p className="text-muted-foreground text-[10px] sm:text-xs">Lote Atual</p>
                    <p className="font-bold text-sm sm:text-lg">{activeBulkSend.current_batch || 1}/{activeBulkSend.total_batches}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-[10px] sm:text-xs">Por Lote</p>
                    <p className="font-bold text-sm sm:text-lg">{activeBulkSend.batch_size}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-[10px] sm:text-xs">Intervalo</p>
                    <p className="font-bold text-sm sm:text-lg">1h</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-[10px] sm:text-xs">Próximo</p>
                    <p className="font-bold text-sm sm:text-lg">
                      {activeBulkSend.batches.find(b => b.status === 'pending') 
                        ? new Date(activeBulkSend.batches.find(b => b.status === 'pending').scheduled_time).toLocaleTimeString('pt-BR', {hour: '2-digit', minute: '2-digit'})
                        : 'Fim'}
                    </p>
                  </div>
                </div>
                
                {/* Lista de lotes */}
                <div className="space-y-1 sm:space-y-2 max-h-[150px] sm:max-h-[200px] overflow-y-auto">
                  {activeBulkSend.batches.map((batch, idx) => (
                    <div 
                      key={idx}
                      className={`flex items-center justify-between p-1.5 sm:p-2 rounded text-xs sm:text-sm ${
                        batch.status === 'completed' ? 'bg-green-500/20' :
                        batch.status === 'scheduled' || batch.status === 'sending' ? 'bg-blue-500/20' :
                        batch.status === 'error' ? 'bg-red-500/20' :
                        'bg-muted/50'
                      }`}
                    >
                      <span>Lote {batch.batch_number}: {batch.count}</span>
                      <Badge variant="outline" className="text-[10px] sm:text-xs">
                        {batch.status === 'completed' ? 'OK' :
                         batch.status === 'scheduled' || batch.status === 'sending' ? 'Enviando' :
                         batch.status === 'error' ? 'Erro' :
                         new Date(batch.scheduled_time).toLocaleTimeString('pt-BR', {hour: '2-digit', minute: '2-digit'})}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Controles */}
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3">
              {(bulkProgress?.status === 'sending' || bulkProgress?.status === 'scheduled' || activeBulkSend.status === 'sending') && (
                <Button
                  variant="outline"
                  onClick={pauseBulkSend}
                  className="w-full sm:flex-1 sm:min-w-[120px] h-9 sm:h-10 text-sm"
                  data-testid="pause-btn"
                >
                  <Pause className="w-4 h-4 mr-1 sm:mr-2" />
                  Pausar
                </Button>
              )}
              
              {(bulkProgress?.status === 'paused' || activeBulkSend.status === 'paused') && (
                <Button
                  onClick={resumeBulkSend}
                  className="w-full sm:flex-1 sm:min-w-[120px] bg-green-600 hover:bg-green-700 h-9 sm:h-10 text-sm"
                  data-testid="resume-btn"
                >
                  <Play className="w-4 h-4 mr-1 sm:mr-2" />
                  Retomar
                </Button>
              )}
              
              {bulkProgress?.status !== 'completed' && bulkProgress?.status !== 'cancelled' && (
                <Button
                  variant="destructive"
                  onClick={cancelBulkSend}
                  className="w-full sm:flex-1 sm:min-w-[120px] h-9 sm:h-10 text-sm"
                  data-testid="cancel-btn"
                >
                  <Square className="w-4 h-4 mr-1 sm:mr-2" />
                  Cancelar
                </Button>
              )}
              
              {(bulkProgress?.status === 'completed' || bulkProgress?.status === 'cancelled') && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => deleteBulkSend(activeBulkSend.id)}
                    className="w-full sm:flex-1 sm:min-w-[120px] h-9 sm:h-10 text-sm"
                  >
                    <Trash2 className="w-4 h-4 mr-1 sm:mr-2" />
                    Excluir
                  </Button>
                  <Button
                    onClick={resetAll}
                    className="w-full sm:flex-1 sm:min-w-[120px] bg-gradient-to-r from-purple-600 to-pink-600 h-9 sm:h-10 text-sm"
                    data-testid="new-bulk-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-1 sm:mr-2" />
                    Novo Envio
                  </Button>
                </>
              )}
            </div>
            
            {/* Mensagem de conclusão */}
            {bulkProgress?.status === 'completed' && (
              <div className="p-6 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-xl border border-green-500/20 text-center">
                <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
                <p className="text-xl font-bold text-green-600">Envio Concluído!</p>
                <p className="text-muted-foreground">
                  {bulkProgress.sent} mensagens enviadas com sucesso
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialog de Histórico */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Histórico de Envios em Massa</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {sendHistory.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Nenhum envio em massa realizado ainda
              </p>
            ) : (
              sendHistory.map(bulk => (
                <div 
                  key={bulk.id} 
                  className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Badge className={`${getStatusColor(bulk.status)} text-white`}>
                        {getStatusText(bulk.status)}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {new Date(bulk.created_at).toLocaleString('pt-BR')}
                      </span>
                    </div>
                    <p className="text-sm mt-1">
                      {bulk.total_numbers} números • Tipo: {bulk.message_type}
                    </p>
                    {bulk.sent_count !== undefined && (
                      <p className="text-xs text-muted-foreground">
                        Enviados: {bulk.sent_count || 0} | Falhas: {bulk.failed_count || 0}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {bulk.status === 'sending' || bulk.status === 'paused' || bulk.status === 'scheduled' ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setActiveBulkSend(bulk);
                          setCurrentStep(5);
                          startProgressPolling(bulk.id);
                          setShowHistory(false);
                        }}
                      >
                        Ver Progresso
                      </Button>
                    ) : null}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteBulkSend(bulk.id)}
                      className="text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistory(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
